import { apiFetch } from './client'
import { ApiError } from './errors'
import type { JobLevel, JobStatus } from './jobs'

/**
 * Tela 5 (sessao 38) - ficha do cargo, SO LEITURA.
 *
 * Contratos (lidos na DEFINICAO, modules/job_architecture/api):
 *   GET /api/jobs/{id}                          -> JobResponseDTO   (job_architecture.job.read)
 *   GET /api/jobs/{id}/descriptions/current     -> JobDescriptionResponseDTO (description.read)
 *        404 com code `job_description_not_found` = o cargo NAO tem versao PUBLICADA
 *        (inclusive quando so tem rascunho) -> a tela cai no texto legado (Q5).
 *   GET /api/jobs/{id}/descriptions             -> DescriptionVersionSummaryDTO[] (description.read)
 * Os 5 papeis tem as duas permissoes de leitura (seeds rbac de cargos e de descricao).
 *
 * S27-6 (cargo SEM salario): o mapeamento e CAMPO A CAMPO; `salary_range` nao tem onde
 * morar no tipo `JobRecord`, entao nenhum componente consegue mostra-lo por engano.
 *
 * D4 (s38): SO o 404 com o codigo `job_description_not_found` vira "descricao antiga".
 * Qualquer outro erro (403, 500, ou 404 SEM esse codigo -- ex.: API antiga sem a rota)
 * e ERRO: dizer "nao ha versao" quando a pergunta nem chegou ao servidor seria mentira.
 *
 * D7 (s38): ids de usuario (`published_by_user_id`, `created_by_user_id`) NAO entram nos
 * tipos -- id opaco nao diz nada a quem le e nao deve ir para a tela.
 */

export type DescriptionBlock =
  | 'papel'
  | 'entregas'
  | 'detalhamento'
  | 'conhecimentos'
  | 'formacao'
  | 'treinamentos'
  | 'experiencia'
  | 'soft_skills'

export type ItemRequirement = 'necessario' | 'desejavel'

export interface JobRecord {
  id: string
  code: string
  name: string
  level: JobLevel | null
  careerName: string | null
  cboCode: string | null
  status: JobStatus
  /** Texto legado `Job.description` (Q5: so leitura). */
  legacyDescription: string | null
}

interface JobRecordResponse {
  id: string
  code: string
  name: string
  level: JobLevel | null
  career_name: string | null
  cbo_code: string | null
  status: JobStatus
  description: string | null
}

export interface DescriptionItem {
  id: string
  block: DescriptionBlock
  position: number
  text: string
  requirement: ItemRequirement | null
  servesToItemId: string | null
}

interface DescriptionItemResponse {
  id: string
  block: DescriptionBlock
  position: number
  text: string
  requirement: ItemRequirement | null
  serves_to_item_id: string | null
}

export interface PublishedDescription {
  id: string
  versionNumber: number
  funcao: string | null
  areaName: string | null
  reportsToName: string | null
  publishedAt: string | null
  items: DescriptionItem[]
}

interface DescriptionResponse {
  id: string
  version_number: number
  status: string
  funcao: string | null
  area_unit_name: string | null
  reports_to_job_name: string | null
  published_at: string | null
  items: DescriptionItemResponse[]
}

export type DescriptionStatus = 'rascunho' | 'publicada'

export interface VersionSummary {
  id: string
  versionNumber: number
  status: DescriptionStatus
  publishedAt: string | null
}

interface VersionSummaryResponse {
  id: string
  version_number: number
  status: DescriptionStatus
  published_at: string | null
}

export const DESCRIPTION_NOT_FOUND_CODE = 'job_description_not_found'

function toJobRecord(r: JobRecordResponse): JobRecord {
  // Campo a campo, de proposito: nada de salario (S27-6).
  return {
    id: r.id,
    code: r.code,
    name: r.name,
    level: r.level,
    careerName: r.career_name,
    cboCode: r.cbo_code,
    status: r.status,
    legacyDescription: r.description,
  }
}

function toItem(r: DescriptionItemResponse): DescriptionItem {
  return {
    id: r.id,
    block: r.block,
    position: r.position,
    text: r.text,
    requirement: r.requirement,
    servesToItemId: r.serves_to_item_id,
  }
}

export async function fetchJobRecord(jobId: string): Promise<JobRecord> {
  return toJobRecord(await apiFetch<JobRecordResponse>(`/api/jobs/${encodeURIComponent(jobId)}`))
}

function isNoPublishedVersion(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 404) return false
  const d = e.detail
  return typeof d === 'object' && d !== null && (d as { code?: unknown }).code === DESCRIPTION_NOT_FOUND_CODE
}

/** A versao PUBLICADA vigente; `null` SO quando o servidor diz que ela nao existe (D4). */
export async function fetchCurrentDescription(jobId: string): Promise<PublishedDescription | null> {
  let r: DescriptionResponse
  try {
    r = await apiFetch<DescriptionResponse>(`/api/jobs/${encodeURIComponent(jobId)}/descriptions/current`)
  } catch (e) {
    if (isNoPublishedVersion(e)) return null
    throw e
  }
  return {
    id: r.id,
    versionNumber: r.version_number,
    funcao: r.funcao,
    areaName: r.area_unit_name,
    reportsToName: r.reports_to_job_name,
    publishedAt: r.published_at,
    items: r.items.map(toItem),
  }
}

export async function fetchDescriptionVersions(jobId: string): Promise<VersionSummary[]> {
  const r = await apiFetch<VersionSummaryResponse[]>(`/api/jobs/${encodeURIComponent(jobId)}/descriptions`)
  return r.map((v) => ({
    id: v.id,
    versionNumber: v.version_number,
    status: v.status,
    publishedAt: v.published_at,
  }))
}

// ------------------------------------------------------------------ agrupamento

export interface DeliveryGroup {
  entrega: DescriptionItem
  atividades: DescriptionItem[]
}

export interface RequirementColumns {
  necessario: DescriptionItem[]
  desejavel: DescriptionItem[]
}

export interface GroupedDescription {
  papel: DescriptionItem[]
  entregas: DeliveryGroup[]
  /** D1: atividades sem entrega (ou com ligacao que nao aponta para uma entrega desta versao). */
  transversais: DescriptionItem[]
  formacao: RequirementColumns
  conhecimentos: RequirementColumns
  treinamentos: RequirementColumns
  experiencia: DescriptionItem[]
  softSkills: DescriptionItem[]
}

function doBloco(items: readonly DescriptionItem[], b: DescriptionBlock): DescriptionItem[] {
  return items.filter((i) => i.block === b).sort((a, z) => a.position - z.position)
}

function colunas(items: readonly DescriptionItem[], b: DescriptionBlock): RequirementColumns {
  const doB = doBloco(items, b)
  return {
    necessario: doB.filter((i) => i.requirement === 'necessario'),
    // Exigencia ausente (nao deveria ocorrer, I-B0a 6c) cai em "desejavel": nao some.
    desejavel: doB.filter((i) => i.requirement !== 'necessario'),
  }
}

/**
 * Monta a ficha no layout do PES.001: cada ENTREGA com as ATIVIDADES (detalhamento)
 * que servem a ela, na ordem de posicao; atividades sem ligacao valida = transversais.
 */
export function groupDescription(d: Pick<PublishedDescription, 'items'>): GroupedDescription {
  const entregas = doBloco(d.items, 'entregas')
  const idsEntrega = new Set(entregas.map((e) => e.id))
  const atividades = doBloco(d.items, 'detalhamento')
  return {
    papel: doBloco(d.items, 'papel'),
    entregas: entregas.map((e) => ({
      entrega: e,
      atividades: atividades.filter((a) => a.servesToItemId === e.id),
    })),
    transversais: atividades.filter((a) => a.servesToItemId === null || !idsEntrega.has(a.servesToItemId)),
    formacao: colunas(d.items, 'formacao'),
    conhecimentos: colunas(d.items, 'conhecimentos'),
    treinamentos: colunas(d.items, 'treinamentos'),
    experiencia: doBloco(d.items, 'experiencia'),
    softSkills: doBloco(d.items, 'soft_skills'),
  }
}

/**
 * s55b (pedido de Marcelo): o ";" do FIM de cada item vinha da planilha (so para leitura no Excel). A TELA nao o
 * mostra; o dado gravado nao muda (publicadas sao imutaveis). Tira so ";" e espaco do fim -- ponto final e ";" do
 * meio ficam.
 */
export function textoParaTela(texto: string): string {
  return texto.replace(/[\s;]+$/u, '')
}

/** Rascunho em elaboracao (D5), se houver. */
export function findDraft(versions: readonly VersionSummary[]): VersionSummary | null {
  return versions.find((v) => v.status === 'rascunho') ?? null
}

/** Versoes publicadas ANTERIORES a vigente (D7: rodape), mais recente primeiro. */
export function previousPublished(versions: readonly VersionSummary[], currentId: string): VersionSummary[] {
  return versions
    .filter((v) => v.status === 'publicada' && v.id !== currentId)
    .sort((a, z) => z.versionNumber - a.versionNumber)
}

export const jobRecordKey = (id: string) => ['job', id] as const
export const currentDescriptionKey = (id: string) => ['job-description-current', id] as const
export const descriptionVersionsKey = (id: string) => ['job-description-versions', id] as const
